import { useCallback, useRef, useState } from 'react';

/**
 * Tracks whether a PrimeReact Dialog's `.p-dialog-content` scroll container
 * has been scrolled away from the top. Attach the returned `contentRef` to any
 * element that is a descendant of `.p-dialog-content`; the hook will walk up
 * the DOM via `closest()` to find the actual scroll container.
 *
 * Uses a callback ref so the listener is attached the instant the node mounts
 * (no timing issues with `useEffect` + portals) and removed when it unmounts.
 */
export function useScrollShadow() {
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);

  const contentRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      const scrollEl = node.closest('.p-dialog-content') as HTMLElement | null;
      if (scrollEl) {
        scrollContainerRef.current = scrollEl;
        const handleScroll = () => setIsScrolled(scrollEl.scrollTop > 0);
        scrollHandlerRef.current = handleScroll;
        scrollEl.addEventListener('scroll', handleScroll);
      }
    } else {
      if (scrollContainerRef.current && scrollHandlerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', scrollHandlerRef.current);
        scrollContainerRef.current = null;
        scrollHandlerRef.current = null;
      }
      setIsScrolled(false);
    }
  }, []);

  return { isScrolled, contentRef };
}
